/**
 * Auth Store - Authentication state management
 * Tokens are stored in httpOnly cookies (managed by server)
 * Only user info is kept in client state
 */

import { useState, useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as Sentry from '@sentry/react';
import { setBusinessContext } from '@/shared/lib/sentry';
import type { AuthUser } from '@aegislab/shared';

export interface AuthState {
  /** Current authenticated user */
  user: AuthUser | null;
  /** Is authentication loading */
  isLoading: boolean;
  /** Authentication error */
  error: string | null;
  /**
   * 부팅 시 서버 세션 검증이 완료된 시각 (epoch ms).
   * null이면 아직 검증 전. persist에서 제외 — 새 탭/새로고침마다 재검증.
   */
  serverVerifiedAt: number | null;
}

export interface AuthActions {
  /** Set authenticated user */
  setAuth: (user: AuthUser) => void;
  /** Clear authentication */
  clearAuth: () => void;
  /** Clear auth state and invoke the registered logout callback (e.g. redirect) */
  logout: () => void;
  /** Set loading state */
  setLoading: (loading: boolean) => void;
  /** Set error */
  setError: (error: string | null) => void;
  /** 서버 세션 검증 완료 마킹 (성공/실패/스킵 모두). */
  markServerVerified: () => void;
}

// Module-level callback — not in Zustand state so persist is unaffected
let onLogoutCallback: (() => void) | null = null;
export function registerOnLogout(cb: () => void) {
  onLogoutCallback = cb;
}

const initialState: AuthState = {
  user: null,
  isLoading: false,
  error: null,
  serverVerifiedAt: null,
};

/**
 * Auth store with persistence (user info only)
 */
export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...initialState,

      setAuth: (user) => {
        // Sentry 사용자 식별 정보 설정
        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: user.username,
        });
        setBusinessContext({
          teamId: user.teamId,
          teamRole: user.teamRole,
          roles: user.roles,
        });
        set({ user, error: null, serverVerifiedAt: Date.now() });
      },

      clearAuth: () => {
        // Sentry 사용자 정보 제거
        Sentry.setUser(null);
        set(initialState);
      },

      logout: () => {
        // Sentry 사용자 정보 제거
        Sentry.setUser(null);
        set(initialState);
        onLogoutCallback?.();
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      markServerVerified: () => set({ serverVerifiedAt: Date.now() }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user
          ? {
              ...state.user,
              roles: [],
            }
          : null,
      }),
    }
  )
);

/**
 * Returns true once zustand persist has finished rehydrating from localStorage.
 * Use this to prevent route guards from redirecting before auth state is loaded.
 */
export function useAuthHydrated(): boolean {
  const [hydrated, setHydrated] = useState(useAuthStore.persist.hasHydrated());

  useEffect(() => {
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
    return unsub;
  }, []);

  return hydrated;
}

/**
 * Auth selectors for convenience
 */
export const selectUser = (state: AuthState) => state.user;
export const selectIsAuthenticated = (state: AuthState) => !!state.user;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectError = (state: AuthState) => state.error;
